import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FileSignature,
  Box,
  ClipboardList,
  PenTool,
  FileText,
  Factory,
  Truck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Play,
  CheckCircle2,
  MapPin,
  Star,
  Clock,
  Users,
  Shield,
  Phone,
  MessageSquareQuote,
  HelpCircle,
  PackageCheck,
} from 'lucide-react';
import { JOURNEY_STAGES, TRUST_STATS, JourneyStage } from '../utils/journeyContent';
import { subscribeToJourneyData } from '../utils/journeyFirestore';
import { JourneyStageMedia, getStageMediaType } from '../components/journey/JourneyStageMedia';
import './JourneyPage.css';

const STAGE_ICONS: Record<string, React.ReactNode> = {
  'design-fee': <FileSignature size={20} />,
  '3d-model': <Box size={20} />,
  'quote': <ClipboardList size={20} />,
  'quote-signed': <PenTool size={20} />,
  '2d-plans': <FileText size={20} />,
  'in-production': <Factory size={20} />,
  'delivery': <Truck size={20} />,
};

// ─── Stage Q&A Accordion ────────────────────────────────────────────────────

interface QAItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

const QAItem: React.FC<QAItemProps> = ({ question, answer, isOpen, onToggle }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isOpen ? contentRef.current.scrollHeight : 0);
    }
  }, [isOpen]);

  return (
    <div className={`journey-qa-item ${isOpen ? 'is-open' : ''}`}>
      <button className="journey-qa-trigger" onClick={onToggle} aria-expanded={isOpen}>
        <span className="journey-qa-question">{question}</span>
        <ChevronDown size={18} className="journey-qa-chevron" />
      </button>
      <div className="journey-qa-content" style={{ height: `${height}px` }} ref={contentRef}>
        <p className="journey-qa-answer">{answer}</p>
      </div>
    </div>
  );
};

// ─── Testimonial Card ───────────────────────────────────────────────────────

const TestimonialCard: React.FC<{ testimonial: JourneyStage['testimonial'] }> = ({ testimonial }) => (
  <div className="journey-testimonial">
    <div className="journey-testimonial-mark">"</div>
    <blockquote className="journey-testimonial-quote">{testimonial.quote}</blockquote>
    <div className="journey-testimonial-attribution">
      <div className="journey-testimonial-avatar">
        {testimonial.photoUrl ? (
          <img src={testimonial.photoUrl} alt={testimonial.name} />
        ) : (
          <span>{testimonial.name.charAt(0)}</span>
        )}
      </div>
      <div className="journey-testimonial-info">
        <span className="journey-testimonial-name">{testimonial.name}</span>
        <span className="journey-testimonial-meta">
          <MapPin size={12} />
          {testimonial.location} · {testimonial.projectType}
        </span>
      </div>
    </div>
    {testimonial.videoUrl && (
      <button className="journey-testimonial-video-btn">
        <Play size={16} />
        <span>Watch their story</span>
      </button>
    )}
  </div>
);

// ─── Deliverables ───────────────────────────────────────────────────────────

const Deliverables: React.FC<{ deliverables: JourneyStage['deliverables'] }> = ({ deliverables }) => (
  <div className="journey-deliverables">
    <h4 className="journey-deliverables-title">What you'll receive</h4>
    <div className="journey-deliverables-list">
      {deliverables.map((d, i) => (
        <div className="journey-deliverable" key={i}>
          <CheckCircle2 size={16} className="journey-deliverable-icon" />
          <div>
            <span className="journey-deliverable-label">{d.label}</span>
            <span className="journey-deliverable-desc">{d.description}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ─── Stage Section (compact: tabbed bottom for desktop) ─────────────────────

type CardTab = 'qa' | 'testimonial' | 'deliverables';

const StageSection: React.FC<{ stage: JourneyStage; nextStage?: JourneyStage; isVisible: boolean; compact?: boolean; onNextStage?: () => void; onOutro?: () => void }> = ({ stage, nextStage, isVisible, compact = false, onNextStage, onOutro }) => {
  const [expandedQs, setExpandedQs] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<CardTab>('qa');
  const toggleQ = useCallback((id: string) => {
    setExpandedQs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  if (!compact) {
    return (
      <section className={`journey-stage ${isVisible ? 'is-visible' : ''}`} id={`stage-${stage.id}`} data-stage={stage.number}>
        <div className="journey-stage-card">
          <div className="journey-stage-watermark" aria-hidden="true">
            {String(stage.number).padStart(2, '0')}
          </div>
          <div className="journey-stage-header">
            <div className="journey-stage-badge">
              {STAGE_ICONS[stage.id]}
              <span>Stage {stage.number} of 7</span>
              <span className="journey-stage-duration"><Clock size={12} />{stage.duration}</span>
            </div>
            <h2 className="journey-stage-title">{stage.title}</h2>
            <p className="journey-stage-subtitle">{stage.subtitle}</p>
          </div>
          <div className="journey-lead-qa">
            <p className="journey-lead-question">{stage.leadQuestion}</p>
            <p className="journey-lead-answer">{stage.leadAnswer}</p>
          </div>
          <div className="journey-qa-section">
            <h3 className="journey-qa-heading">Common questions at this stage</h3>
            <div className="journey-qa-list">
              {stage.questions.map(q => (
                <QAItem key={q.id} question={q.question} answer={q.answer} isOpen={expandedQs.has(q.id)} onToggle={() => toggleQ(q.id)} />
              ))}
            </div>
          </div>
          <TestimonialCard testimonial={stage.testimonial} />
          <Deliverables deliverables={stage.deliverables} />
          {(stage.nextStageTeaser || (stage.number === 7 && onOutro)) && (
            <div className="journey-next-teaser">
              <div className="journey-next-teaser-label">Up next</div>
              <div className="journey-next-teaser-row">
                {nextStage && (
                  <div className="journey-next-teaser-stage">
                    Stage {nextStage.number} · {nextStage.title}
                  </div>
                )}
                {nextStage && onNextStage && (
                  <button type="button" className="journey-next-teaser-btn" onClick={onNextStage}>
                    Next Step
                    <ChevronDown size={18} />
                  </button>
                )}
                {stage.number === 7 && onOutro && (
                  <button type="button" className="journey-next-teaser-btn" onClick={onOutro}>
                    Next Steps
                    <ChevronDown size={18} />
                  </button>
                )}
              </div>
              {stage.nextStageTeaser && <p className="journey-next-teaser-text">{stage.nextStageTeaser}</p>}
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className={`journey-stage journey-stage-compact ${isVisible ? 'is-visible' : ''}`} id={`stage-${stage.id}`} data-stage={stage.number}>
      <div className="journey-compact-top">
        <div className="journey-compact-stage-num">
          <span className="journey-compact-stage-current">{String(stage.number).padStart(2, '0')}</span>
          <span className="journey-compact-stage-sep">/</span>
          <span className="journey-compact-stage-total">{String(7).padStart(2, '0')}</span>
        </div>
        <h2 className="journey-compact-title">{stage.title}</h2>
        <div className="journey-compact-meta">
          <span className="journey-compact-subtitle">{stage.subtitle}</span>
          <span className="journey-stage-duration"><Clock size={12} />{stage.duration}</span>
        </div>
      </div>

      <div className="journey-compact-lead">
        <p className="journey-compact-lead-q">{stage.leadQuestion}</p>
        <p className="journey-compact-lead-a">{stage.leadAnswer}</p>
      </div>

      <div className="journey-compact-tabs">
        <button className={`journey-compact-tab ${activeTab === 'qa' ? 'is-active' : ''}`} onClick={() => setActiveTab('qa')}>
          <HelpCircle size={14} /> <span>FAQ</span>
        </button>
        <button className={`journey-compact-tab ${activeTab === 'testimonial' ? 'is-active' : ''}`} onClick={() => setActiveTab('testimonial')}>
          <MessageSquareQuote size={14} /> <span>Story</span>
        </button>
        <button className={`journey-compact-tab ${activeTab === 'deliverables' ? 'is-active' : ''}`} onClick={() => setActiveTab('deliverables')}>
          <PackageCheck size={14} /> <span>Deliverables</span>
        </button>
      </div>

      <div className="journey-compact-panel">
        {activeTab === 'qa' && (
          <div className="journey-compact-panel-content">
            {stage.questions.map(q => (
              <QAItem key={q.id} question={q.question} answer={q.answer} isOpen={expandedQs.has(q.id)} onToggle={() => toggleQ(q.id)} />
            ))}
          </div>
        )}
        {activeTab === 'testimonial' && (
          <div className="journey-compact-panel-content">
            <TestimonialCard testimonial={stage.testimonial} />
          </div>
        )}
        {activeTab === 'deliverables' && (
          <div className="journey-compact-panel-content">
            <Deliverables deliverables={stage.deliverables} />
          </div>
        )}
      </div>

      {(stage.nextStageTeaser || (stage.number === 7 && onOutro)) && (
        <div className="journey-compact-teaser">
          <div className="journey-compact-teaser-inner">
            <span className="journey-compact-teaser-label">Up next</span>
            <div className="journey-compact-teaser-row">
              {nextStage && (
                <span className="journey-compact-teaser-stage">Stage {nextStage.number} · {nextStage.title}</span>
              )}
              {nextStage && onNextStage && (
                <button type="button" className="journey-compact-teaser-btn" onClick={onNextStage}>
                  Next Step
                  <ChevronDown size={16} />
                </button>
              )}
              {stage.number === 7 && onOutro && (
                <button type="button" className="journey-compact-teaser-btn" onClick={onOutro}>
                  Next Steps
                  <ChevronDown size={16} />
                </button>
              )}
            </div>
            {stage.nextStageTeaser && <p className="journey-compact-teaser-text">{stage.nextStageTeaser}</p>}
          </div>
        </div>
      )}
    </section>
  );
};

// ─── Progress Navigator ─────────────────────────────────────────────────────

interface ProgressNavProps {
  activeStage: number;
  isVisible: boolean;
  stages: JourneyStage[];
  cardsScrollRef?: React.RefObject<HTMLDivElement | null>;
}

const ProgressNav: React.FC<ProgressNavProps> = ({ activeStage, isVisible, stages, cardsScrollRef }) => {
  const scrollToStage = (n: number) => {
    const el = document.getElementById(`stage-${stages[n - 1].id}`);
    const container = cardsScrollRef?.current;
    if (container && el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <nav className={`journey-progress ${isVisible ? 'is-visible' : ''}`} aria-label="Journey progress">
      <div className="journey-progress-inner">
        <div className="journey-progress-track">
          <div className="journey-progress-fill" style={{ width: `${((activeStage - 1) / (stages.length - 1)) * 100}%` }} />
        </div>
        <div className="journey-progress-dots">
          {stages.map((stage) => (
            <button
              key={stage.id}
              className={`journey-progress-dot ${stage.number <= activeStage ? 'is-active' : ''} ${stage.number === activeStage ? 'is-current' : ''}`}
              onClick={() => scrollToStage(stage.number)}
              aria-label={`Stage ${stage.number}: ${stage.title}`}
              title={stage.title}
            >
              <span className="journey-progress-dot-num">{stage.number}</span>
            </button>
          ))}
        </div>
      </div>
      <span className="journey-progress-label">{stages[activeStage - 1]?.title}</span>
    </nav>
  );
};

// ─── 3D Loading Fallback ────────────────────────────────────────────────────

const SceneLoading: React.FC = () => (
  <div className="journey-scene-loading">
    <div className="journey-scene-loading-spinner" />
    <span>Loading 3D model…</span>
  </div>
);

// ─── Main Page ──────────────────────────────────────────────────────────────

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : false
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    setIsDesktop(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
};

const JourneyPage: React.FC = () => {
  const [stages, setStages] = useState<JourneyStage[]>(JOURNEY_STAGES);
  const [trustStats, setTrustStats] = useState(TRUST_STATS);
  const [ctaUrl, setCtaUrl] = useState('https://www.lctimberframes.com/contact');
  const [ctaLabel, setCtaLabel] = useState('Speak with the Founder');
  const [journeyMedia, setJourneyMedia] = useState<{ droneDesign?: string; cnc?: string; crewRaising?: string; model3d?: string; model3dEngineering?: string } | null>(null);
  const [activeStage, setActiveStage] = useState(1);
  const [showProgress, setShowProgress] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<number>>(new Set());
  const [assemblyProgress, setAssemblyProgress] = useState(0);
  const [isModelHovering, setIsModelHovering] = useState(false);
  const [modelMousePos, setModelMousePos] = useState({ x: 0, y: 0 });
  const [modelRotation, setModelRotation] = useState({ x: 0, y: 0 });
  const [isModelDragging, setIsModelDragging] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [hasDismissedHoverHint, setHasDismissedHoverHint] = useState(false);
  const lastPointerRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const [searchParams] = useSearchParams();
  const isDesktop = useIsDesktop();
  const stageRefs = useRef<(HTMLElement | null)[]>([]);
  const heroRef = useRef<HTMLDivElement>(null);
  const splitRef = useRef<HTMLDivElement>(null);
  const cardsScrollRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsub = subscribeToJourneyData((data) => {
      if (data.stages?.length) setStages(data.stages);
      if (data.trustStats) setTrustStats(data.trustStats);
      if (data.settings?.ctaUrl) setCtaUrl(data.settings.ctaUrl);
      if (data.settings?.ctaLabel) {
        const label = data.settings.ctaLabel === 'Book a Consultation' ? 'Speak with the Founder' : data.settings.ctaLabel;
        setCtaLabel(label);
      }
      if (data.media) setJourneyMedia(data.media);
    });
    return unsub;
  }, []);

  const utmSource = searchParams.get('utm_source');
  const refId = searchParams.get('ref');
  useEffect(() => {
    if (utmSource || refId) console.log('[Journey] Visitor source:', { utmSource, refId });
  }, [utmSource, refId]);

  // Stage section observers (mobile only; desktop uses scroll-driven activeStage)
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    if (heroRef.current) {
      const ho = new IntersectionObserver(([e]) => setShowProgress(!e.isIntersecting), { threshold: 0.1 });
      ho.observe(heroRef.current);
      observers.push(ho);
    }
    if (!isDesktop) {
      const so = new IntersectionObserver(
        (entries) => entries.forEach(e => {
          const n = Number(e.target.getAttribute('data-stage'));
          if (e.isIntersecting) {
            setActiveStage(n);
            setVisibleSections(prev => new Set(prev).add(n));
          }
        }),
        { threshold: 0.15, rootMargin: '-60px 0px -35% 0px' }
      );
      stageRefs.current.forEach(r => { if (r) so.observe(r); });
      observers.push(so);
    }
    return () => observers.forEach(o => o.disconnect());
  }, [isDesktop]);

  const [heroFade, setHeroFade] = useState(1);
  const [outroFade, setOutroFade] = useState(0);
  const [timelineProgress, setTimelineProgress] = useState(0);

  // Scroll tracking: desktop = dissolve hero→cards→outro (throttled via rAF to reduce CPU load)
  useEffect(() => {
    let rafId: number | null = null;
    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (window.scrollY > 30) setHasScrolled(true);
        if (isDesktop) {
        const vh = window.innerHeight;
        const fadeZone = vh * 0.6;
        const fade = 1 - Math.min(1, window.scrollY / fadeZone);
        setHeroFade(fade);

        const stageStart = vh;
        const scrollIntoStages = window.scrollY - stageStart;
        if (scrollIntoStages < 0) {
          setActiveStage(1);
          setVisibleSections(prev => new Set(prev).add(1));
          setAssemblyProgress(0);
          setOutroFade(0);
          setTimelineProgress(0);
          return;
        }
        const cardIndex = Math.floor(scrollIntoStages / vh);
        const stage = Math.min(stages.length, Math.max(1, cardIndex + 1));
        setActiveStage(stage);
        setVisibleSections(prev => new Set(prev).add(stage));
        const maxScroll = stages.length * vh;
        const raw = maxScroll > 0 ? Math.min(1, scrollIntoStages / maxScroll) : 0;
        setAssemblyProgress(raw);

        const segmentProgress = (scrollIntoStages % vh) / vh;
        const smoothProgress = (cardIndex + segmentProgress) / Math.max(1, stages.length - 1);
        setTimelineProgress(Math.min(1, smoothProgress));

        const outroBuffer = vh * 1.5;
        const outroStart = stageStart + (stages.length - 1) * vh + outroBuffer;
        const outroZone = vh * 3;
        const scrollPastLast = window.scrollY - outroStart;
        let outro = Math.min(1, Math.max(0, scrollPastLast / outroZone));
        const maxScrollY = document.documentElement.scrollHeight - window.innerHeight;
        if (window.scrollY >= maxScrollY - 20) outro = 1;
        setOutroFade(outro);
      }
    });
  };
    const handlePageScroll = () => {
      if (!splitRef.current || isDesktop) return;
      const rect = splitRef.current.getBoundingClientRect();
      const sectionHeight = splitRef.current.offsetHeight;
      const vh = window.innerHeight;
      const raw = (vh - rect.top) / (sectionHeight + vh);
      setAssemblyProgress(Math.max(0, Math.min(1, raw)));
    };
    const isSnappingRef = { current: false };
    const snapOutroZone = () => {
      if (!isDesktop || isSnappingRef.current) return;
      const vh = window.innerHeight;
      const stageStart = vh;
      const outroBuffer = vh * 1.5;
      const outroStart = stageStart + (stages.length - 1) * vh + outroBuffer;
      const outroZone = vh * 3;
      const scrollPastLast = window.scrollY - outroStart;
      if (scrollPastLast > 0 && scrollPastLast < outroZone) {
        isSnappingRef.current = true;
        window.scrollTo({ top: outroStart + outroZone, behavior: 'smooth' });
        const clearSnapping = () => { isSnappingRef.current = false; };
        if ('onscrollend' in window) {
          const onDone = () => { window.removeEventListener('scrollend', onDone); clearSnapping(); };
          window.addEventListener('scrollend', onDone);
        } else {
          setTimeout(clearSnapping, 800);
        }
      }
    };

    let scrollEndTimer: ReturnType<typeof setTimeout>;
    const handleScrollEnd = () => {
      clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(snapOutroZone, 150);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('scroll', handleScrollEnd, { passive: true });
    if ('onscrollend' in window) {
      window.addEventListener('scrollend', snapOutroZone);
    }
    if (!isDesktop) window.addEventListener('scroll', handlePageScroll, { passive: true });
    handleScroll();
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScrollEnd);
      if ('onscrollend' in window) {
        window.removeEventListener('scrollend', snapOutroZone);
      }
      if (!isDesktop) window.removeEventListener('scroll', handlePageScroll);
      clearTimeout(scrollEndTimer);
    };
  }, [isDesktop, stages.length]);

  const setStageRef = useCallback((i: number) => (el: HTMLElement | null) => {
    stageRefs.current[i] = el;
  }, []);

  const handleModelMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    setModelMousePos({ x: nx, y: ny });

    if (isDraggingRef.current) {
      const dx = e.clientX - lastPointerRef.current.x;
      setModelRotation(prev => ({
        x: 0,
        y: prev.y + dx * 0.004,
      }));
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
    }
  }, []);

  const handleModelPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    isDraggingRef.current = true;
    setIsModelDragging(true);
    lastPointerRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleModelPointerUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsModelDragging(false);
  }, []);

  const handleModelPointerLeave = useCallback(() => {
    isDraggingRef.current = false;
    setIsModelDragging(false);
    setIsModelHovering(false);
  }, []);

  useEffect(() => {
    const onUp = () => {
      isDraggingRef.current = false;
      setIsModelDragging(false);
    };
    window.addEventListener('pointerup', onUp);
    return () => window.removeEventListener('pointerup', onUp);
  }, []);

  const scrollToFirstStage = () => {
    if (isDesktop) {
      const vh = window.innerHeight;
      window.scrollTo({ top: vh, behavior: 'smooth' });
    } else {
      document.getElementById(`stage-${stages[0].id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToStage = (n: number) => {
    if (isDesktop) {
      const vh = window.innerHeight;
      const targetY = vh + (n - 1) * vh;
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    } else {
      document.getElementById(`stage-${stages[n - 1].id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToOutro = () => {
    if (isDesktop) {
      const vh = window.innerHeight;
      const stageStart = vh;
      const outroBuffer = vh * 1.5;
      const outroZone = vh * 3;
      const outroStart = stageStart + (stages.length - 1) * vh + outroBuffer;
      window.scrollTo({ top: outroStart + outroZone, behavior: 'smooth' });
    } else {
      document.getElementById('journey-outro')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const showExplorer = isDesktop && heroFade < 0.5 && outroFade < 0.95;
  const mediaType = getStageMediaType(activeStage);
  const isModelStage = mediaType === '3d' || mediaType === 'engineering';

  useEffect(() => {
    if (activeStage === 1 && isModelHovering) setHasDismissedHoverHint(true);
  }, [activeStage, isModelHovering]);

  return (
    <div className="journey-page">

      {/* ─── Hero (fixed, fades out on scroll) ───────────────────────── */}
      <header
        className="journey-hero"
        ref={heroRef}
        style={isDesktop ? { opacity: heroFade, pointerEvents: heroFade < 0.1 ? 'none' : 'auto' } : undefined}
      >
        <div className="journey-hero-bg" />
        <div className="journey-hero-content">
          <div className="journey-hero-eyebrow">
            <Shield size={14} />
            <span>A Proven Process</span>
          </div>
          <h1 className="journey-hero-title">
            Your Timber Frame<br /><em>Journey</em>
          </h1>
          <p className="journey-hero-description">
            Every LC Timber Frames project follows the same {stages.length}-stage process.
            No guesswork. No surprises. Just a clear path from first conversation
            to the day your frame stands.
          </p>
          <div className="journey-hero-stats">
            <div className="journey-hero-stat">
              <span className="journey-hero-stat-value">{trustStats.familiesServed}+</span>
              <span className="journey-hero-stat-label">Families Served</span>
            </div>
            <div className="journey-hero-stat-divider" />
            <div className="journey-hero-stat">
              <span className="journey-hero-stat-value">{trustStats.averageRating}</span>
              <span className="journey-hero-stat-label"><Star size={12} className="journey-star-icon" /> Average Rating</span>
            </div>
            <div className="journey-hero-stat-divider" />
            <div className="journey-hero-stat">
              <span className="journey-hero-stat-value">{trustStats.statesServed}</span>
              <span className="journey-hero-stat-label">States Served</span>
            </div>
          </div>
          <div className="journey-hero-timeline">
            {stages.map((stage) => (
              <button
                key={stage.id}
                type="button"
                className="journey-hero-timeline-step"
                onClick={() => scrollToStage(stage.number)}
              >
                <span className="journey-hero-timeline-num">{String(stage.number).padStart(2, '0')}</span>
                <span className="journey-hero-timeline-name">{stage.title}</span>
              </button>
            ))}
          </div>
          <button className="journey-hero-cta" onClick={scrollToFirstStage}>
            <ChevronDown size={18} />
            <span>Scroll to explore</span>
          </button>
        </div>
      </header>

      {/* ─── Progress Nav ────────────────────────────────────────────── */}
      <ProgressNav activeStage={activeStage} isVisible={showProgress} stages={stages} cardsScrollRef={cardsScrollRef} />

      {/* ─── Stage-based media (3D / drone / engineering / CNC / crew raising) ─ */}
      {isDesktop && (
        <div
          className={`journey-3d-bg ${isModelStage ? 'is-model-stage' : ''} ${isModelDragging ? 'is-dragging' : ''}`}
          ref={bgRef}
          style={{ opacity: (1 - heroFade) * (1 - outroFade) }}
          onMouseEnter={() => isModelStage && setIsModelHovering(true)}
          onMouseMove={isModelStage ? handleModelMouseMove : undefined}
          onMouseLeave={handleModelPointerLeave}
          onPointerDown={isModelStage ? handleModelPointerDown : undefined}
          onPointerUp={handleModelPointerUp}
        >
          {activeStage === 1 && !hasDismissedHoverHint && (
            <div className="journey-hover-hint" aria-hidden>
              Hover to Explode
            </div>
          )}
          <JourneyStageMedia
            activeStage={activeStage}
            media={journeyMedia}
            show3d
            sceneProps={{
              assemblyProgress,
              activeStage,
              isHovering: isModelHovering,
              isDragging: isModelDragging,
              mousePosition: modelMousePos,
              modelRotation,
            }}
          />
        </div>
      )}

      {/* ─── Mobile Model Hero (visible < 1024px only) ───────────────── */}
      {!isDesktop && (
        <div className="journey-model-hero">
          <JourneyStageMedia
            activeStage={activeStage}
            media={journeyMedia}
            show3d
            sceneProps={{ assemblyProgress, activeStage }}
            sceneFallback={<SceneLoading />}
          />
        </div>
      )}

      {/* ─── Full-width stage timeline header ────────────────────────── */}
      {isDesktop && (
        <div
          className={`journey-stage-header-bar ${showExplorer ? 'is-visible' : ''}`}
          style={{ opacity: showExplorer ? 1 - outroFade : 0 }}
        >
          <div className="journey-tl">
            <div className="journey-tl-track">
              <div className="journey-tl-fill" style={{ width: `${((1 + 12 * timelineProgress) / 14) * 100}%` }} />
            </div>
            {stages.map((stage) => (
              <button
                key={stage.id}
                type="button"
                className={`journey-tl-step ${activeStage === stage.number ? 'is-active' : ''} ${activeStage > stage.number ? 'is-past' : ''}`}
                onClick={() => scrollToStage(stage.number)}
              >
                <span className="journey-tl-num">{stage.number}</span>
                <span className="journey-tl-label">{stage.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── Stage Cards (dissolve transition on desktop, scroll on mobile) ─── */}
      <div
        className={`journey-stages-wrapper ${showExplorer ? 'is-visible' : ''}`}
        ref={splitRef}
        style={isDesktop ? { opacity: showExplorer ? 1 - outroFade : 0 } : undefined}
      >
        <div
          className={`journey-cards-scroll ${isDesktop ? 'journey-cards-scroll-desktop' : ''}`}
          ref={cardsScrollRef}
        >
          {stages.map((stage, i) => (
            <div
              key={stage.id}
              ref={setStageRef(i)}
              data-stage={stage.number}
              className={`journey-card ${isDesktop ? 'journey-card-stack' : ''} ${activeStage === stage.number ? 'is-active' : ''}`}
            >
              <StageSection stage={stage} nextStage={stages[i + 1]} isVisible={visibleSections.has(stage.number)} compact={isDesktop} onNextStage={stages[i + 1] ? () => scrollToStage(stages[i + 1].number) : undefined} onOutro={stage.number === 7 ? scrollToOutro : undefined} />
            </div>
          ))}
        </div>
        {isDesktop && (
          <div className="journey-cards-scroll-indicator">
            <ChevronDown size={14} />
            <span>Scroll through stages</span>
          </div>
        )}
      </div>
      {isDesktop && <div className="journey-cards-spacer" aria-hidden="true" />}

      {/* ─── CTA + Footer (dissolves in after last stage) ─────────────── */}
      <div
        id="journey-outro"
        className="journey-outro"
        style={isDesktop ? { opacity: outroFade, pointerEvents: outroFade > 0.3 ? 'auto' : 'none' } : undefined}
      >
        <section className="journey-cta-section">
          <div className="journey-cta-inner">
            <h2 className="journey-cta-title">Every family's journey is unique.<br /><em>The path is proven.</em></h2>
            <p className="journey-cta-description">
              Ready to see what a timber frame could look like for your home?
              Start with a conversation — no commitment, no pressure. Just answers.
            </p>
            <div className="journey-cta-actions">
              <a href={ctaUrl} className="journey-cta-primary"><Phone size={18} /><span>{ctaLabel}</span></a>
            </div>
          </div>
        </section>

        <footer className="journey-footer">
          <div className="journey-footer-inner">
            <div className="journey-footer-brand">
              <strong>LC Timber Frames</strong>
              <span>Handcrafted in Vermont</span>
            </div>
            <div className="journey-footer-stats">
              <span><Users size={14} /> {trustStats.familiesServed}+ families served</span>
              <span><Star size={14} /> {trustStats.averageRating} average rating ({trustStats.reviewCount} reviews)</span>
              <span><MapPin size={14} /> Serving {trustStats.statesServed} states across New England & beyond</span>
            </div>
            <div className="journey-footer-links">
              <a href="https://www.lctimberframes.com">Main Site</a>
              <a href="https://www.lctimberframes.com/gallery">Gallery</a>
              <a href="https://www.lctimberframes.com/contact">Contact</a>
            </div>
            <p className="journey-footer-copy">© {new Date().getFullYear()} LC Timber Frames. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default JourneyPage;
